import {
    createColumnVisibilityMenu,
    exportAntTableData,
} from "@/ts/revstack/helpers";
import { buildRevstackTableHeader } from "@/ts/revstack/ui-utils";
import { Skeleton, Table } from "antd";
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import NoDataText from "./NoDataText";
import {
    CommonWidgetMetrics,
    TableWorkerType,
    TableWorkerResult,
    IProductGroup,
} from "@ts/common-types";
import { createWorkers } from "@ts/utils";
import { LiaChevronCircleUpSolid, LiaChevronCircleDownSolid } from "react-icons/lia";

interface IExpandIcon {
    expanded: boolean;
    onExpand: (record: any, e: React.MouseEvent<HTMLElement>) => void;
    record: any;
}

const ExpandIcon: React.FC<IExpandIcon> = ({ expanded, onExpand, record }) => (
    <span
        className="d-flex align-items-center justify-content-center"
        role="button"
        onClick={(e) => onExpand(record, e)}
    >
        {expanded ? (
            <LiaChevronCircleUpSolid size={20} style={{ color: "#919191" }} />
        ) : (
            <LiaChevronCircleDownSolid size={20} style={{ color: "#919191" }} />
        )}
    </span>
);

interface IRevinitiAntTableWidgetProps {
    widgetData: any;
    config: any;
    isLoading: boolean;
    onTableSelection?: (products: any[]) => void;
    selectedRows?: any[];
    handleRowExpand?: (expanded: boolean) => void;
    handleExpandedRowsChange?: (keys: React.Key[]) => void;
    style?: any;
    onRowSelect?: (record: IProductGroup, selected: boolean, selectedRows: IProductGroup[]) => void;
    dataFilter?: <T>(data: T[]) => T[];
    dataSorter?: <T>(data: T[], originalData: T[], sortConfig: { sorter?: (a: T, b: T) => number; order?: 'ascend' | 'descend' }) => T[];
}

interface ITableComponentProps {
    colDefs: any[];
    originalColDefs: any[];
    type: string;
    selectedCardName: string;
    tableData: any[];
    isLoading: boolean;
    childFilters: any[];
    additionalFilters: any[];
    noPadding: boolean;
    isGroupedTable: boolean;
    isTitleRequired: boolean;
    isLayoutButtonReq: boolean;
    isDownloadButtonReq: boolean;
    isCheckboxSelectionRequired: boolean;
    isSelectAllRequired: boolean;
    isCompetitionButtonReq: boolean;
    isSearchReq: boolean;
    isExpandRowsButtonReq: boolean;
    isDrilldownTable: boolean;
    style: any;
    bordered: boolean;
    searchHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
    searchValue: string;
    columnVisibilityMap: any;
    onColumnToggle: (columnKey: string) => void;
    colVisibilityMode: "group" | "columns";
    onTableSelection: (products: any[]) => void;
    selectedRows: any[];
    expandedTableData: any[];
    expandedRowKeys: React.Key[];
    expandAllRows: boolean;
    setExpandAllRows: (expandAllRows: boolean) => void;
    setExpandedRowKeys: (expandedRowKeys: React.Key[]) => void;
    setLoadedChunks: (loadedChunks: number) => void;
    setInternalSelectedRowKeys: (internalSelectedRowKeys: React.Key[]) => void;
    setPriorityUpdateCounter: (priorityUpdateCounter: number) => void;
    setSelectedFilter: (selectedFilter: string) => void;
    setCompetitionFilteredData: (competitionFilteredData: any) => void;
    setTableHeight: (tableHeight: number) => void;
    isExpandable: boolean;
    showExpandColumn?: boolean;
    handleRowExpand?: (expanded: boolean) => void;
    handleExpandedRowsChange?: (keys: React.Key[]) => void;
    expandedRowRender?: (record: any) => React.ReactNode;
    rowExpandable?: (record: any) => boolean;
    priorityUpdateCounter: number;
    categoryOptions?: Array<{ label: string; value: string }>;
    subcategoryOptions?: Array<{ label: string; value: string }>;
    onRowSelect?: (record: IProductGroup, selected: boolean, selectedRows: IProductGroup[]) => void;
    dataFilter?: <T>(data: T[]) => T[];
    dataSorter?: <T>(data: T[], originalData: T[], sortConfig: { sorter?: (a: T, b: T) => number; order?: 'ascend' | 'descend' }) => T[];
}

const TableComponent: React.FC<ITableComponentProps> = ({
    colDefs,
    originalColDefs,
    type = "category",
    selectedCardName,
    tableData,
    isLoading = false,
    childFilters,
    additionalFilters,
    noPadding = false,
    isGroupedTable = false,
    isTitleRequired = false,
    isLayoutButtonReq = false,
    isDownloadButtonReq = false,
    isCheckboxSelectionRequired = false,
    isSelectAllRequired = false,
    isCompetitionButtonReq = false,
    isSearchReq = false,
    isExpandRowsButtonReq = false,
    isDrilldownTable = false,
    style,
    bordered = true,
    searchHandler = (_e: React.ChangeEvent<HTMLInputElement>) => {
        /* Handle search */
    },
    searchValue = "",
    columnVisibilityMap = {},
    onColumnToggle = (_key: string) => {
        /* Handle column toggle */
    },
    colVisibilityMode = "columns",
    onTableSelection,
    selectedRows: externalSelectedRows,
    isExpandable = false,
    showExpandColumn = false,
    handleRowExpand,
    handleExpandedRowsChange,
    expandedRowKeys,
    setExpandedRowKeys,
    expandedRowRender,
    rowExpandable,
    priorityUpdateCounter,
    onRowSelect,
    dataFilter,
    dataSorter,
}) => {
    const [selectedFilter, setSelectedFilter] = useState<string>(
        childFilters?.[0]?.defaultFilter || ""
    );
    const [isRowExpanded, setIsRowExpanded] = useState<boolean>(false);
    const [competitionFilteredData, setCompetitionFilteredData] =
        useState<any>(null);
    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<
        React.Key[]
    >([]);
    // State to track current table sorting
    const [currentSortConfig, setCurrentSortConfig] = useState<{
        columnKey?: string;
        order?: 'ascend' | 'descend';
        sorter?: (a: any, b: any) => number;
    }>({});
    
    // Derive selectedRowKeys from selectedRows or use internal state
    const selectedRowKeys = useMemo(() => {
        // If we have external selectedRows, derive keys from it
        if (externalSelectedRows) {
            return externalSelectedRows.map((row: any) => String(row.key));
        }
        // Otherwise use internal state
        return internalSelectedRowKeys;
    }, [externalSelectedRows, internalSelectedRowKeys]);

    // Row selection handlers
    const handleRowSelectionChange = (
        newSelectedRowKeys: React.Key[],
        newSelectedRows: any[]
    ) => {
        // If we're not using external control, update internal state
        if (!externalSelectedRows) {
            setInternalSelectedRowKeys(newSelectedRowKeys);
        }

        // Always notify parent of changes via onTableSelection with full row data
        if (onTableSelection) {
            // Ensure we're passing the complete row data
            const selectedRowsData = newSelectedRows.map(row => ({
                ...row,
                key: generateRowKey(row, tableData?.indexOf(row) || 0)
            }));
            onTableSelection(selectedRowsData);
        }
    };

    const handleSelectAll = (
        selected: boolean,
        allSelectedRows: any[],
        changeRows: any[]
    ) => {
        // will do something here
    };

    const handleSelectRow = (
        record: any,
        selected: boolean,
        allSelectedRows: IProductGroup[]
    ) => {
        // Delegate to custom handler if provided
        if (onRowSelect) {
            onRowSelect(record, selected, allSelectedRows);
        }
    };

    // Row selection configuration
    const rowSelection = isCheckboxSelectionRequired
        ? {
              selectedRowKeys, // Always provide selectedRowKeys
              onChange: handleRowSelectionChange,
              onSelectAll: handleSelectAll,
              hideSelectAll: !isSelectAllRequired,
              onSelect: handleSelectRow,
              type: "checkbox" as const,
              preserveSelectedRowKeys: true,
              columnWidth: 60,
              getCheckboxProps: (record: any) => ({
                  name: record.name || record.title || record.id || record.keyword,
                  disabled: false, // Enable selection for all rows
                  key: generateRowKey(record, dataSource.indexOf(record)),
              }),
          }
        : undefined;

    const DRILLDOWN_HEADER_HEIGHT = 360;
    const [tableHeight, setTableHeight] = useState<number>(
        document?.documentElement?.clientHeight - DRILLDOWN_HEADER_HEIGHT || 0
    );

    useEffect(() => {
        if (isLoading) {
            setSelectedFilter(childFilters?.[0]?.defaultFilter || "");
        }
    }, [isLoading, childFilters]);

    // Reset competition filtered data when tableData changes
    useEffect(() => {
        setCompetitionFilteredData(null);
    }, [tableData]);


    const computedFilterOptions = useMemo(() => {
        const primaryFilter = childFilters?.[0];
        if (
            !primaryFilter?.filterOptions ||
            primaryFilter?.filterOptions?.length === 0
        ) {
            return [];
        }

        return primaryFilter?.filterOptions?.map((option: any) => {
            let count = 0;

            if (primaryFilter?.type === "SELECT" && primaryFilter?.dataKey) {
                const filterKey =
                    primaryFilter?.mappedDataKey || primaryFilter?.dataKey;
                if (option?.value === CommonWidgetMetrics.All) {
                    count = tableData?.length || 0;
                } else {
                    count =
                        tableData?.filter(
                            (item: any) => item[filterKey] === option?.value
                        ).length || 0;
                }
            } else {
                // For radio filters, use direct key access
                count = tableData?.[option.value]?.length || 0;
            }

            return {
                ...option,
                count,
            };
        });
    }, [tableData, childFilters]);

    const filteredData = useMemo(() => {
        if (!tableData || !Array.isArray(tableData)) return tableData;

        // Apply custom data filter if provided
        let filtered = dataFilter ? dataFilter(tableData) : [...tableData];

        // Also apply primary filter if it exists (for backward compatibility)
        const primaryFilter = childFilters?.[0];
        if (primaryFilter?.type === "SELECT" && primaryFilter?.dataKey && selectedFilter && selectedFilter !== CommonWidgetMetrics.All) {
            const filterKey = primaryFilter?.mappedDataKey || primaryFilter?.dataKey;
            filtered = filtered.filter((item: any) => item[filterKey] === selectedFilter);
        }

        return filtered;
    }, [tableData, dataFilter, selectedFilter, childFilters]);

    const showNoData = useMemo(() => {
        if (isSearchReq && searchValue && searchValue.trim() !== "") {
            return false;
        }

        if (computedFilterOptions.length > 0) {
            const primaryFilter = childFilters?.[0];
            const isDropdownFilter = primaryFilter?.type === "SELECT";
            if (isDropdownFilter) {
                return (filteredData?.length || 0) === 0;
            }
            // For radio filters like laggards/leaders, check if selected filter has no data
            return (
                (tableData?.[selectedFilter as keyof typeof tableData]
                    ?.length || 0) === 0
            );
        }
        return (tableData?.length || 0) === 0;
    }, [
        tableData,
        computedFilterOptions,
        filteredData,
        childFilters,
        selectedFilter,
        isSearchReq,
        searchValue,
    ]);

    const fullDataSource = useMemo(() => {
        // Use competition filtered data if available, otherwise use filtered data (which includes category/subcategory filters)
        const baseData = competitionFilteredData || filteredData || tableData;

        // Ensure we always return an array
        return Array.isArray(baseData) ? baseData : [];
    }, [filteredData, tableData, competitionFilteredData]);

    // Return full data source with optional custom sorting
    const dataSource = useMemo(() => {
        if (!fullDataSource || !Array.isArray(fullDataSource)) {
            return fullDataSource;
        }

        // If custom dataSorter is provided, use it
        if (dataSorter) {
            return dataSorter(fullDataSource, tableData, currentSortConfig);
        }

        // Default behavior: apply column sorter if available
        if (currentSortConfig.sorter && currentSortConfig.order) {
            const sorted = [...fullDataSource].sort((a, b) => {
                const sortResult = currentSortConfig.sorter!(a, b);
                return currentSortConfig.order === 'ascend' ? sortResult : -sortResult;
            });
            return sorted;
        }

        return fullDataSource;
    }, [fullDataSource, tableData, dataSorter, priorityUpdateCounter, currentSortConfig]);

    const generateRowKey = useCallback((row: any, index: number): string => {
        // Prefer explicit key if provided
        if (row?.key) return String(row.key);
        // For campaign collections, use clusterId-collectionId format
        if (row?.clusterId && row?.id) {
            return `${row.clusterId}-${row.id}`;
        }
        // For keyword rows, fallback to a keyword-based key
        if (row?.keyword) {
            return `keyword-${row.keyword}-${index}`;
        }
        // Fallback to existing logic
        if (row?.id) return String(row.id);
        return `row-${index}`;
    }, []);

    useEffect(() => {
        return () => {
            setSelectedFilter("");
            setIsRowExpanded(false);
            setCompetitionFilteredData(null);
            setTableHeight(
                document?.documentElement?.clientHeight -
                    DRILLDOWN_HEADER_HEIGHT || 0
            );
        };
    }, []);

    const originalColumnMenuItems = useMemo(() => {
        return createColumnVisibilityMenu(
            originalColDefs || colDefs,
            colVisibilityMode
        );
    }, [originalColDefs, colDefs, colVisibilityMode]);

    // Create a map of column keys to original sorters for quick lookup
    const columnSorterMap = useMemo(() => {
        const map = new Map<string, (a: any, b: any) => number>();
        colDefs?.forEach((col: any) => {
            if (col.sorter && typeof col.sorter === 'function') {
                // Try multiple key formats to match columns
                const keys: string[] = [];
                if (col.key) keys.push(String(col.key));
                if (col.dataIndex) {
                    if (Array.isArray(col.dataIndex)) {
                        keys.push(col.dataIndex.join('.'));
                    } else {
                        keys.push(String(col.dataIndex));
                    }
                }
                // Add all keys to the map
                keys.forEach(key => {
                    if (key) {
                        map.set(key, col.sorter);
                    }
                });
            }
        });
        return map;
    }, [colDefs]);

    // Handler for table sorting changes
    const handleTableChange = useCallback((pagination: any, filters: any, sorter: any) => {
        if (sorter && (sorter.columnKey || sorter.field) && sorter.order) {
            const columnKey = sorter.columnKey || sorter.field;
            // Get the original sorter function from our map
            const sorterFn = columnSorterMap.get(String(columnKey));
            
            if (sorterFn && typeof sorterFn === 'function') {
                setCurrentSortConfig({
                    columnKey: columnKey,
                    order: sorter.order,
                    sorter: sorterFn,
                });
            } else {
                // If no sorter function, clear the sort config
                setCurrentSortConfig({});
            }
        } else {
            // No sorting applied, clear the sort config
            setCurrentSortConfig({});
        }
    }, [columnSorterMap]);

    // Auto-trigger market share descending sort when search is active
    const wasSearchActiveRef = useRef(false);
    useEffect(() => {
        const isSearchActive = searchValue && searchValue.trim() !== "";
        const wasSearchActive = wasSearchActiveRef.current;
        wasSearchActiveRef.current = isSearchActive;

        if (isSearchActive) {
            const sorterFn = columnSorterMap.get(`${CommonWidgetMetrics.MARKET_SHARE}_pct`);
            if (sorterFn) {
                setCurrentSortConfig({
                    columnKey: `${CommonWidgetMetrics.MARKET_SHARE}_pct`,
                    order: 'descend',
                    sorter: sorterFn,
                });
            }
        } else if (wasSearchActive) {
            setCurrentSortConfig({});
        }
        // If search is inactive and was already inactive, don't clear existing manual sorting
    }, [searchValue, columnSorterMap]);

    const modifiedColDefs = useMemo(() => {
        return colDefs?.map((cd: any, index: number) => {
            if (cd?.key === "drilldown_action") {
                return {
                    ...cd,
                    render: (value: any, record: any, rowIndex: number) => {
                        return cd.render(
                            value,
                            { ...record, dataSource: fullDataSource },
                            rowIndex
                        );
                    },
                };
            }

            //!Note[DND]: This we have kep by default for gouped table, if its optional we can add this to config
            // Modify the first pinned column to include row count in header
            // Check for showRowCount property in column definition, default to true if not specified
            const showRowCount = cd?.showRowCount !== false; // Default to true unless explicitly set to false
            if (index === 0 && cd?.fixed === "left" && showRowCount) {
                const rowCount = fullDataSource?.length || 0;

                // Handle different title types
                if (React.isValidElement(cd.title)) {
                    // If title is a React element (from TableTitleCellRenderer)
                    const titleText = cd.title.props.children || "";
                    return {
                        ...cd,
                        title: React.cloneElement(cd.title, {
                            ...cd.title.props,
                            children: `${titleText} (${rowCount})`,
                        }),
                        sorter: (_a: any, _b: any) => {
                            return 0;
                        }

                        // Force width constraints for fixed columns
                        // width: cd.width,
                        // minWidth: cd.width,
                        // maxWidth: cd.width,
                        // onHeaderCell: () => ({
                        //     style: {
                        //         width: `${cd.width}px`,
                        //         minWidth: `${cd.width}px`,
                        //         maxWidth: `${cd.width}px`,
                        //     },
                        // }),
                        // onCell: () => ({
                        //     style: {
                        //         width: `${cd.width}px`,
                        //         minWidth: `${cd.width}px`,
                        //         maxWidth: `${cd.width}px`,
                        //     },
                        // }),
                    };
                } else if (typeof cd.title === "string") {
                    return {
                        ...cd,
                        title: `${cd.title} (${rowCount})`,
                        sorter: (_a: any, _b: any) => {
                            return 0;
                        }
                        // Force width constraints for fixed columns
                        // width: cd.width,
                        // minWidth: cd.width,
                        // maxWidth: cd.width,
                        // onHeaderCell: () => ({
                        //     style: {
                        //         width: `${cd.width}px`,
                        //         minWidth: `${cd.width}px`,
                        //         maxWidth: `${cd.width}px`,
                        //     },
                        // }),
                        // onCell: () => ({
                        //     style: {
                        //         width: `${cd.width}px`,
                        //         minWidth: `${cd.width}px`,
                        //         maxWidth: `${cd.width}px`,
                        //     },
                        // }),
                    };
                }
            }

            // Enforce width constraints for any fixed column
            // if (cd?.fixed && cd?.width) {
            //     return {
            //         ...cd,
            //         width: cd.width,
            //         minWidth: cd.width,
            //         maxWidth: cd.width,
            //         onHeaderCell: () => ({
            //             style: {
            //                 width: `${cd.width}px`,
            //                 minWidth: `${cd.width}px`,
            //                 maxWidth: `${cd.width}px`,
            //             },
            //         }),
            //         onCell: () => ({
            //             style: {
            //                 width: `${cd.width}px`,
            //                 minWidth: `${cd.width}px`,
            //                 maxWidth: `${cd.width}px`,
            //             },
            //         }),
            //     };
            // }

            return {
                ...cd,
                sorter: (_a: any, _b: any) => {
                    return 0;
                }
            };
        });
    }, [colDefs, fullDataSource, searchValue]);

    const calculateTotalColumnWidth = useMemo(() => {
        if (!modifiedColDefs || modifiedColDefs.length === 0) {
            return 1500;
        }

        const totalWidth = modifiedColDefs.reduce((sum: number, col: any) => {
            const colWidth = col.width || col.minWidth || 120;
            return sum + colWidth;
        }, 0);

        return totalWidth;
    }, [modifiedColDefs]);

    return (
        <div
            className="d-flex flex-column"
            style={{
                padding: 0,
                height: "100%",
                flex: 1,
            }}
        >
            {isLoading ? (
                <Skeleton.Node
                    className="w-100"
                    active
                    style={{ height: style?.height || 400 }}
                />
            ) : (
                <>
                    {buildRevstackTableHeader({
                        noPadding,
                        isSearchReq,
                        data: filteredData,
                        isCompetitionButtonReq,
                        isTitleRequired,
                        isLayoutButtonReq,
                        isDownloadButtonReq,
                        type: type as "category" | "subcategory" | "regional",
                        selectedCardName,
                        childFilters,
                        additionalFilters: additionalFilters || [],
                        computedFilterOptions,
                        setSelectedFilter,
                        layoutConfig: null,
                        searchHandler,
                        createColumnVisibilityMenu: () =>
                            originalColumnMenuItems,
                        downloadButtonHandler: (ext) =>
                            exportAntTableData(
                                fullDataSource,
                                modifiedColDefs,
                                ext
                            ),
                        searchValue: searchValue,
                        isExpandRowsButtonReq,
                        columnVisibilityMap,
                        onColumnToggle,
                        justifyContent: style?.justifyHeaderContent || "",
                    })}

                    {showNoData ? (
                        <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                                height: style?.height || 400,
                                border: noPadding
                                    ? "none"
                                    : "1px solid #e8e8e8",
                                borderRadius: noPadding ? "0 0 8px 8px" : "0",
                            }}
                        >
                            <NoDataText />
                        </div>
                    ) : (
                        <>
                            {isGroupedTable || isDrilldownTable ? (
                                <Table
                                    key="grouped-table"
                                    className="revstack-grouped-table"
                                    bordered={bordered}
                                    size="middle"
                                    tableLayout="fixed"
                                    virtual={true}
                                    columns={modifiedColDefs || []}
                                    dataSource={dataSource}
                                    rowKey={(row, idx) =>
                                        generateRowKey(row, idx || 0)
                                    }
                                    rowClassName={(record) =>
                                        record?.isSection
                                            ? "section-row"
                                            : record?.disabled
                                            ? "disabled-row"
                                            : ""
                                    }
                                    rowSelection={rowSelection}
                                    onChange={handleTableChange}
                                    scroll={{
                                        x: calculateTotalColumnWidth,
                                        y: style?.disableScrollY 
                                            ? undefined 
                                            : isDrilldownTable
                                            ? tableHeight
                                            : isRowExpanded
                                            ? style?.expandedHeight || 360
                                            : style?.height || 400,
                                    }}
                                    pagination={false}
                                    style={
                                        !isDrilldownTable
                                            ? {
                                                  height: "390px",
                                              }
                                            : {}
                                    }
                                />
                            ) : (
                                <Table
                                    rowKey={(row, idx) =>
                                        generateRowKey(row, idx || 0)
                                    }
                                    className="revstack-table"
                                    size="middle"
                                    columns={modifiedColDefs}
                                    dataSource={dataSource}
                                    pagination={false}
                                    bordered={bordered}
                                    virtual
                                    rowClassName={(record) =>
                                        record?.disabled ? "disabled-row" : "align-middle"
                                    }
                                    rowSelection={rowSelection}
                                    onChange={handleTableChange}
                                    scroll={{
                                        x: calculateTotalColumnWidth,
                                        y: style?.disableScrollY 
                                            ? undefined 
                                            : isDrilldownTable
                                            ? tableHeight
                                            : isRowExpanded
                                            ? style?.expandedHeight || 360
                                            : style?.height || 200,
                                    }}
                                    {...(isExpandable && {
                                        expandable: {
                                            expandedRowKeys,
                                            onExpandedRowsChange: (
                                                keys: readonly React.Key[]
                                            ) => {
                                                const newKeys = [...keys];
                                                setExpandedRowKeys(newKeys);
                                                if (handleRowExpand) {
                                                    handleRowExpand(newKeys.length > 0);
                                                }
                                                if (handleExpandedRowsChange) {
                                                    handleExpandedRowsChange(newKeys);
                                                }
                                            },
                                            expandedRowRender: expandedRowRender,
                                            ...(rowExpandable && { rowExpandable }),
                                            showExpandColumn: showExpandColumn,
                                            expandIconColumnIndex: 0,
                                            expandIcon: ExpandIcon,
                                            columnWidth: 30,
                                            expandedRowClassName: () => "expanded-row-no-padding",
                                        },
                                    })}
                                    components={{
                                        header: {
                                            cell: (props: any) => (
                                                <th
                                                    {...props}
                                                    style={{
                                                        ...props.style,
                                                        height: "50px",
                                                        fontSize: "12px",
                                                        fontWeight: "700",
                                                        whiteSpace: "normal",
                                                        textOverflow:
                                                            "ellipsis",
                                                        verticalAlign: "middle",
                                                        wordBreak: "break-word",
                                                        lineHeight: "1.2",
                                                        color: "#2D2D2D",
                                                    }}
                                                />
                                            ),
                                        },
                                        // body: {
                                        //     cell: (props: any) => (
                                        //         <td
                                        //             {...props}
                                        //             style={{
                                        //                 ...props.style,
                                        //                 ...(isTableOnlyMode && {
                                        //                     paddingLeft: 20,
                                        //                     paddingRight: 35,
                                        //                 }),
                                        //             }}
                                        //         />
                                        //     ),
                                        //     row: (props: any) => (
                                        //         <tr
                                        //             {...props}
                                        //             style={{
                                        //                 ...props.style,
                                        //                 height: "60px",
                                        //             }}
                                        //         />
                                        //     ),
                                        // },
                                    }}
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

const RevinitiAntTableWidget: React.FC<IRevinitiAntTableWidgetProps> = ({
    widgetData,
    isLoading,
    config,
    onTableSelection,
    selectedRows,
    handleRowExpand,
    style,
    onRowSelect,
    dataFilter,
    dataSorter,
}) => {
    const [searchValue, setSearchValue] = useState<string>("");
    const [filteredTableData, setFilteredTableData] = useState(
        widgetData.tableData
    );
    const [isProcessing, setIsProcessing] = useState(false);

    // Handle search using web worker
    const handleSearch = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setSearchValue(value);
            setIsProcessing(true);

            try {
                if (!Array.isArray(widgetData.tableData)) {
                    setFilteredTableData([]);
                    return;
                }

                if (!value || value.trim() === "") {
                    setFilteredTableData(widgetData.tableData);
                    return;
                }

                if (window.Worker) {
                    const result = (await createWorkers("table", {
                        type: TableWorkerType.FILTER_DATA,
                        payload: {
                            data: widgetData.tableData,
                            searchValue: value,
                        },
                    })) as { type: TableWorkerResult; payload: any[] };

                    if (result.type === TableWorkerResult.FILTER_DATA_RESULT) {
                        setFilteredTableData(result.payload);
                    }
                } else {
                    // Fallback if web workers are not available
                    const searchLower = value.toLowerCase().trim();
                    const filtered = widgetData.tableData.filter((row: any) => {
                        if (!row) return false;
                        return Object.values(row).some((cellValue: any) => {
                            if (cellValue === null || cellValue === undefined)
                                return false;
                            const stringValue = String(cellValue).toLowerCase();
                            return stringValue.includes(searchLower);
                        });
                    });
                    setFilteredTableData(filtered);
                }
            } catch (error) {
                console.error("Error filtering data:", error);
                setFilteredTableData(widgetData.tableData);
            } finally {
                setIsProcessing(false);
            }
        },
        [widgetData.tableData]
    );

    // Update filtered data when widgetData changes
    useEffect(() => {
        setFilteredTableData(widgetData.tableData);
    }, [widgetData.tableData]);

    // Listen for priority updates and force re-render
    const [priorityUpdateCounter, setPriorityUpdateCounter] = useState(0);
    useEffect(() => {
        const handlePriorityUpdate = () => {
            setPriorityUpdateCounter((prev) => prev + 1);
        };

        window.addEventListener("priorityUpdated", handlePriorityUpdate);
        return () =>
            window.removeEventListener("priorityUpdated", handlePriorityUpdate);
    }, []);

    return (
        <TableComponent
            tableData={filteredTableData}
            expandedTableData={widgetData.expandedTableData}
            isLoading={isLoading}
            {...config}
            onTableSelection={onTableSelection}
            searchHandler={handleSearch}
            searchValue={searchValue}
            selectedRows={selectedRows}
            noPadding={true}
            handleRowExpand={handleRowExpand}
            priorityUpdateCounter={priorityUpdateCounter}
            dataFilter={dataFilter}
            dataSorter={dataSorter}
            style={style}
            onRowSelect={onRowSelect}
        />
    );
};

export default RevinitiAntTableWidget;